import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { Phone, ArrowRight, MapPin, CheckCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import { MobileCTA } from '../components/MobileCTA';
import { businessConfig, trackPhoneClick } from '../config/business';

export default function AreasPage() {
  const { serviceAreas, contact } = businessConfig;

  return (
    <div className="min-h-screen bg-white">
      <Helmet>
        <title>Melbourne Removalists Service Areas | South-East, East & All Suburbs | Thrifty Move</title>
        <meta name="description" content="Thrifty Move services all Melbourne suburbs from $130/hr. Strong coverage across South-East and Eastern Melbourne. Professional removalists with 2 movers included." />
        <link rel="canonical" href="https://thrifty-move.com.au/areas" />
        <meta property="og:title" content="Melbourne Service Areas | From $130/hr | Thrifty Move" />
        <meta property="og:description" content="Professional removalists servicing all Melbourne suburbs from $130/hr. Strong coverage in South-East and Eastern Melbourne." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://thrifty-move.com.au/areas" />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Service",
            "serviceType": "Removalist Services",
            "provider": {
              "@type": "MovingCompany",
              "name": "Thrifty Move"
            },
            "areaServed": [
              {"@type": "City", "name": "Melbourne"},
              {"@type": "AdministrativeArea", "name": "South-East Melbourne"},
              {"@type": "AdministrativeArea", "name": "Eastern Melbourne"}
            ]
          })}
        </script>
      </Helmet>

      <Header />

      {/* Hero */}
      <section className="bg-gray-900 pt-28 md:pt-32 pb-16">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center">
            <div className="inline-flex items-center gap-2 bg-yellow-500 px-4 py-2 rounded-full mb-6">
              <MapPin className="w-5 h-5 text-gray-900" />
              <span className="text-gray-900 font-bold">Melbourne Removalists</span>
            </div>
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
              Service Areas
            </h1>
            <p className="text-xl text-gray-300">
              {serviceAreas.description}
            </p>
          </div>
        </div>
      </section>

      {/* Service Areas */}
      <section className="py-16 md:py-20">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            
            {/* Highlighted Regions */}
            {serviceAreas.regions.filter(r => r.highlight).map((region, i) => (
              <Card key={i} className="border-2 border-yellow-400 bg-yellow-50 mb-6">
                <CardContent className="p-6">
                  <div className="flex items-center gap-2 mb-4">
                    <MapPin className="w-5 h-5 text-yellow-600" />
                    <h2 className="text-xl font-bold text-gray-900">{region.name}</h2>
                    <span className="bg-yellow-500 text-gray-900 text-xs font-bold px-2 py-1 rounded">Strong Coverage</span>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {region.suburbs.map(suburb => (
                      <span 
                        key={suburb} 
                        className="bg-white text-gray-700 px-3 py-1.5 rounded-full text-sm border border-yellow-200 font-medium"
                      >
                        {suburb}
                      </span>
                    ))}
                  </div>
                </CardContent>
              </Card>
            ))}

            {/* Other Regions */}
            <h3 className="text-lg font-bold text-gray-900 mt-10 mb-4">All Melbourne Suburbs</h3>
            {serviceAreas.regions.filter(r => !r.highlight).map((region, i) => (
              <Card key={i} className="border border-gray-200 mb-4">
                <CardContent className="p-5">
                  <div className="flex items-center gap-2 mb-3">
                    <MapPin className="w-4 h-4 text-gray-500" />
                    <h3 className="font-bold text-gray-900">{region.name}</h3>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {region.suburbs.map(suburb => (
                      <span 
                        key={suburb} 
                        className="bg-gray-100 text-gray-600 px-3 py-1 rounded-full text-sm"
                      >
                        {suburb}
                      </span>
                    ))}
                  </div>
                </CardContent>
              </Card>
            ))}

            {/* Don't See Your Suburb */}
            <Card className="border-2 border-gray-200 mt-10">
              <CardContent className="p-6 text-center">
                <h3 className="text-xl font-bold text-gray-900 mb-2">Don't see your suburb?</h3>
                <p className="text-gray-600 mb-6">
                  We service all Melbourne suburbs. Get in touch and we'll confirm availability for your area.
                </p>
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <Link to="/quote">
                    <Button className="w-full sm:w-auto bg-yellow-500 hover:bg-yellow-400 text-gray-900 font-bold px-6 py-3">
                      Get a Quote for Your Area
                    </Button>
                  </Link>
                  <a href={contact.phoneLink} onClick={() => trackPhoneClick('Areas - Call')}>
                    <Button variant="outline" className="w-full sm:w-auto border-gray-900 text-gray-900 hover:bg-gray-900 hover:text-white font-semibold px-6 py-3">
                      <Phone className="mr-2 w-4 h-4" />
                      {contact.phone}
                    </Button>
                  </a>
                </div>
              </CardContent>
            </Card>

          </div>
        </div>
      </section>

      {/* What We Offer */}
      <section className="py-12 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-2xl font-bold text-gray-900 mb-6 text-center">Melbourne Moving Services Include</h2>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              {[
                'House removals',
                'Apartment moves',
                'Office relocations',
                'Furniture delivery',
                'Packing services',
                'Unpacking help'
              ].map((service, i) => (
                <div key={i} className="flex items-center gap-2">
                  <CheckCircle className="w-5 h-5 text-yellow-500" />
                  <span className="text-gray-700">{service}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-yellow-500">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold text-gray-900 mb-4">
            Moving in Melbourne?
          </h2>
          <p className="text-gray-800 mb-8 max-w-xl mx-auto">
            Get your free quote and see transparent pricing for your move.
          </p>
          <Link to="/quote">
            <Button className="bg-gray-900 hover:bg-gray-800 text-white font-bold px-10 py-5 text-lg">
              <ArrowRight className="mr-2 w-5 h-5" />
              Get Your Free Quote
            </Button>
          </Link>
        </div>
      </section>

      <Footer />
      <MobileCTA />
    </div>
  );
}
